import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Phone, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  RefreshCw,
  Edit3,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// SEC-003 — sentinelle pour passer les guards `if (!token)`; le cookie HttpOnly est envoyé via axios withCredentials.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PhoneVerificationModal - Modale de vérification OTP
 * 
 * Utilisable depuis:
 * - Le bandeau de complétion de profil
 * - Le wizard de complétion de profil
 * - Les paramètres de notification
 */
const PhoneVerificationModal = ({ 
  open, 
  onClose, 
  onVerified,
  phone: initialPhone,
  allowPhoneEdit = false 
}) => {
  const [step, setStep] = useState('send'); // 'send', 'verify', 'success'
  const [phone, setPhone] = useState(initialPhone || '');
  const [otpCode, setOtpCode] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [canResendAt, setCanResendAt] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const [error, setError] = useState('');
  
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep('send');
      setOtpCode(['', '', '', '']);
      setError('');
      setPhone(initialPhone || '');
      setRemainingAttempts(5);
    }
  }, [open, initialPhone]);

  // Countdown timer for resend
  useEffect(() => {
    if (!canResendAt) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const resendTime = new Date(canResendAt);
      const diff = Math.max(0, Math.ceil((resendTime - now) / 1000));
      setCountdown(diff);
      
      if (diff === 0) {
        setCanResendAt(null);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [canResendAt]);

  // SEC-003 — sentinelle (auth réelle via cookie HttpOnly + axios withCredentials)
  const getToken = () => 'cookie-auth';

  const sendOtp = async () => {
    if (!phone) {
      toast.error('Veuillez entrer un numéro de téléphone');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/phone-verification/send-otp`,
        { phone },
        {}
      );

      if (response.data.success) {
        setMaskedPhone(response.data.masked_phone);
        setCanResendAt(response.data.can_resend_at);
        setStep('verify');
        toast.success('Code envoyé !');
        
        // Focus first OTP input
        setTimeout(() => inputRefs[0].current?.focus(), 100);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur lors de l\'envoi du code';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    const code = otpCode.join('');
    
    if (code.length !== 4) {
      toast.error('Veuillez entrer le code complet');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/phone-verification/verify-otp`,
        { phone, otp_code: code },
        {}
      );

      if (response.data.success) {
        setStep('success');
        toast.success('Numéro vérifié avec succès !');
        
        // Callback après succès
        setTimeout(() => {
          onVerified?.();
          onClose();
        }, 1500);
      }
    } catch (err) {
      const data = err.response?.data;
      const message = data?.detail || 'Code invalide';
      
      setError(message);
      
      if (data?.remaining_attempts !== undefined) {
        setRemainingAttempts(data.remaining_attempts);
      }
      
      // Clear OTP inputs on error
      setOtpCode(['', '', '', '']);
      inputRefs[0].current?.focus();
      
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    setError('');
    
    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
    
    // Auto-submit when complete
    if (index === 3 && value && newOtp.every(d => d !== '')) {
      setTimeout(() => verifyOtp(), 100);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 4).split('');
        const newOtp = [...otpCode];
        digits.forEach((d, i) => {
          if (i < 4) newOtp[i] = d;
        });
        setOtpCode(newOtp);
        if (digits.length === 4) {
          setTimeout(() => verifyOtp(), 100);
        }
      });
    }
  };

  const handleResend = () => {
    if (countdown > 0) return;
    setOtpCode(['', '', '', '']);
    sendOtp();
  };

  const handleEditPhone = () => {
    setStep('send');
    setOtpCode(['', '', '', '']);
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            )}
            {step === 'success' ? 'Numéro vérifié' : 'Vérification du numéro'}
          </DialogTitle>
          <DialogDescription>
            {step === 'send' && 'Un code à 4 chiffres sera envoyé à votre numéro.'}
            {step === 'verify' && `Un code a été envoyé au ${maskedPhone}`}
            {step === 'success' && 'Votre numéro de téléphone a été vérifié avec succès.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Step: Enter Phone */}
          {step === 'send' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Numéro de téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="tel"
                    placeholder="+225 07 XX XX XX XX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={!allowPhoneEdit && initialPhone}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}

          {/* Step: Enter OTP */}
          {step === 'verify' && (
            <div className="space-y-4">
              {/* OTP Input */}
              <div className="flex justify-center gap-3">
                {otpCode.map((digit, index) => (
                  <Input
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-14 h-14 text-center text-2xl font-bold"
                    disabled={loading}
                  />
                ))}
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-between items-center text-sm">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResend}
                  disabled={countdown > 0 || loading}
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${countdown > 0 ? '' : ''}`} />
                  {countdown > 0 ? `Renvoyer (${countdown}s)` : 'Renvoyer le code'}
                </Button>
                
                {allowPhoneEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditPhone}
                    disabled={loading}
                    className="text-gray-500"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                )}
              </div>
              
              {remainingAttempts < 5 && (
                <p className="text-xs text-center text-amber-600">
                  {remainingAttempts} tentative(s) restante(s)
                </p>
              )}
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-center text-gray-600">
                Votre numéro <strong>{maskedPhone}</strong> est maintenant vérifié.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'send' && (
            <>
              <Button variant="ghost" onClick={onClose} disabled={loading}>
                Annuler
              </Button>
              <Button 
                onClick={sendOtp} 
                disabled={loading || !phone}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Envoyer le code
              </Button>
            </>
          )}
          
          {step === 'verify' && (
            <>
              <Button variant="ghost" onClick={onClose} disabled={loading}>
                Annuler
              </Button>
              <Button 
                onClick={verifyOtp} 
                disabled={loading || otpCode.some(d => !d)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Valider
              </Button>
            </>
          )}
          
          {step === 'success' && (
            <Button 
              onClick={onClose}
              className="bg-emerald-600 hover:bg-emerald-700 w-full"
            >
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneVerificationModal;
